/**
 * Identifies match / day feedback that was abandoned so it can be omitted from averages.
 * Uses API flags when present; falls back to abandoned submission shape (scores & remarks N/A; legacy AB).
 */

/** Stored in API for abandoned match/day rows; keep legacy "AB" readable as abandoned. */
export const ABANDONED_FEEDBACK_MARKER = 'N/A';

const strLower = (v) => String(v ?? '').trim().toLowerCase();

const isAbandonMarkerString = (raw) => {
  const u = String(raw ?? '').trim().toUpperCase();
  return u === 'AB' || u === 'N/A';
};

const scoreValueIsAbandoned = (raw) => {
  if (isAbandonMarkerString(raw)) return true;
  const n = Number(raw);
  if (Number.isFinite(n) && n >= 1 && n <= 5) return false;
  return false;
};

const entryFeedbackIsOnlyAbandonMarkers = (entry) => {
  const remarks = String(entry?.remarks ?? '').trim().toUpperCase();
  if (remarks === 'AB' || remarks === 'N/A') return true;

  const scores = entry?.scores;
  if (Array.isArray(scores)) {
    if (scores.length === 0) return false;
    return scores.every((s) => scoreValueIsAbandoned(s?.score ?? s?.value ?? s));
  }
  if (scores && typeof scores === 'object') {
    const vals = Object.values(scores);
    if (vals.length === 0) return false;
    return vals.every((v) => scoreValueIsAbandoned(v));
  }
  return false;
};

/** True when this single feedback row was submitted as abandoned (remarks N/A, or all scores AB/N/A). */
export function isAbandonedFeedbackEntry(entry) {
  return entryFeedbackIsOnlyAbandonMarkers(entry);
}

export function isAbandonMarkerScoreValue(raw) {
  return isAbandonMarkerString(raw);
}

export function isAbandonedMatchFeedback(record) {
  if (!record || typeof record !== 'object') return false;

  if (record.abandoned === true || record.isAbandoned === true) return true;

  for (const s of [record.matchStatus, record.status, record.feedbackStatus]) {
    if (strLower(s) === 'abandoned') return true;
  }

  const match = record.matchId;
  if (match && typeof match === 'object') {
    for (const s of [match.matchStatus, match.status]) {
      if (strLower(s) === 'abandoned') return true;
    }
  }

  const entries = Array.isArray(record.feedback) ? record.feedback : [];
  if (entries.length === 0) return false;

  return entries.every((entry) => entryFeedbackIsOnlyAbandonMarkers(entry));
}

/** One supervisor rating row (per employee) submitted as abandoned / N/A. */
export function isAbandonedSupervisorRatingEntry(entry) {
  if (!entry || typeof entry !== 'object') return false;
  if (isAbandonMarkerString(entry.rating)) return true;

  const rem = String(entry?.remarks ?? '').trim().toUpperCase();
  const abandonRemark = rem === 'N/A' || rem === 'AB';
  if (!abandonRemark) return false;

  const n = Number(entry?.rating);
  return !Number.isFinite(n) || n === 0;
}

/**
 * Whole supervisor feedback document for a match/day was marked abandoned.
 * Uses API flags when present; falls back to “all rating rows are N/A-style”.
 */
export function isAbandonedSupervisorFeedback(record) {
  if (!record || typeof record !== 'object') return false;

  if (record.abandoned === true || record.isAbandoned === true) return true;

  for (const s of [record.matchStatus, record.status, record.feedbackStatus]) {
    if (strLower(s) === 'abandoned') return true;
  }

  const ratings = Array.isArray(record.ratings) ? record.ratings : [];
  if (ratings.length === 0) return false;

  return ratings.every((entry) => isAbandonedSupervisorRatingEntry(entry));
}
